import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { config } from '@/config/env';
import { useAuth } from '@/lib/auth/AuthContext';
import { canAny, type Permission } from '@/lib/auth/permissions';
import { WORKSPACES, etykietaDojrzalosci, sectionPath, visibleSections, type Workspace } from '@/app/workspaces';
import { PasekDoby } from '@/components/PasekDoby';
import { LauncherProduktow } from '@/components/LauncherProduktow';
import { IkonaNawigacji, kluczIkony } from '@/components/IkonyNawigacji';
import { PaletaPolecen, PrzyciskPalety } from '@/components/PaletaPolecen';
import { PrzelacznikPaneli } from '@/components/PrzelacznikPaneli';
import { PodgladRestauracji } from '@/components/PodgladRestauracji';
import { buildInfo } from '@/config/buildInfo';
import { useWydanie, skrotSha } from '@/lib/useWydanie';
import { useOpisZrodla } from '@/lib/useTrybZrodla';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * POWŁOKA „SYGNAŁ" — przeniesienie zaakceptowanego kierunku C z konceptu R5-15-009.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * R5-15-014 — NAWIGACJA DOPROWADZONA DO MAKIETY.
 *
 * Do tej pory szyna miała DWA POZIOMY: pionowe zakładki przestrzeni roboczych, a pod nimi sekcje
 * tylko tej jednej, w której akurat jesteś. Makieta ma jeden poziom: wszystkie cele widoczne od
 * razu, pogrupowane nagłówkami i opisane ikonami. Różnica nie jest kosmetyczna — w modelu
 * dwupoziomowym większość ekranów jest o dwa kliknięcia i o jedno domyślenie się dalej.
 *
 * CO ZOSTAJE NIETKNIĘTE: filtrowanie po uprawnieniach, znaczniki dojrzałości ekranu
 * (`zaplanowane` / `zablokowane`), trasy i cała logika autoryzacji. Zmienia się sposób
 * pokazywania mapy produktu, nie to, co wolno zobaczyć.
 *
 * KONTRAKT DOSTĘPNOŚCI ZMIENIA SIĘ ŚWIADOMIE: `role="tab"` znika, bo nie ma już zakładek —
 * a rola, która opisuje nieistniejący wzorzec, jest gorsza niż jej brak. Testy E2E i jednostkowe
 * zostały zaktualizowane w tym samym commicie; gdyby ich nie tknąć, przechodziłyby na wzorcu,
 * którego produkt już nie realizuje.
 *
 * SZYNA JEST ZAWSZE CIEMNA — także w wariancie jasnym. To nie ozdoba: stały ciemny chrom po lewej
 * daje krawędź, od której odcina się jasne płótno z danymi, więc w ułamku sekundy widać, gdzie
 * kończy się sterowanie, a zaczyna treść.
 */

const SZEROKOSC_SZYNY = 268;

function aktywnaPrzestrzen(pathname: string): Workspace {
  // Najdłuższe dopasowanie wygrywa, żeby '/crm/kampanie' nie trafiło do '/'.
  const match = [...WORKSPACES]
    .filter((w) => w.path !== '/' && (pathname === w.path || pathname.startsWith(`${w.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return match ?? WORKSPACES[0]!;
}

function PasekSrodowiska() {
  const opisZrodla = useOpisZrodla();
  const label =
    config.environment === 'unknown' ? 'ŚRODOWISKO NIEUSTAWIONE' : `ŚRODOWISKO: ${config.environment.toUpperCase()}`;
  const color = config.environment === 'prod' ? 'error' : config.environment === 'test' ? 'warning' : 'default';
  return (
    // Bez zawijania: „TYLKO ODCZYT" spadało do drugiego wiersza i rozpychało pas w pionie.
    // Pas może się KURCZYĆ, a długi opis źródła skraca się wielokropkiem (pełny tekst w `title`).
    // Zmierzone 2026-10-04: przy `flex: none` opis trybu TEST_CONTROLLED wypychał „Wyloguj” poza
    // ekran już przy TT Masters (1512 px przy oknie 1440), a przy Plus Jakarta Sans — 1686 px.
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', flexWrap: { xs: 'wrap', lg: 'nowrap' }, flex: '0 1 auto', minWidth: 0, maxWidth: '100%' }}
    >
      <Chip size="small" color={color} variant="filled" label={label} sx={{ flexShrink: 0 }} />
      <Chip size="small" variant="outlined" label={opisZrodla} title={opisZrodla} sx={{ minWidth: 0 }} />
      {config.readOnly ? <Chip size="small" variant="outlined" label="TYLKO ODCZYT" sx={{ flexShrink: 0 }} /> : null}
    </Stack>
  );
}

export function AppShell() {
  const wydanie = useWydanie();
  const shaWydania = skrotSha(wydanie.data?.sha);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const permissions = auth.permissions;

  const isVisible = (required?: readonly Permission[]) => canAny(permissions, required);

  const workspace = aktywnaPrzestrzen(location.pathname);
  // Przestrzeń znika z paska dopiero, gdy NIC w niej nie jest widoczne dla tej osoby.
  const widocznePrzestrzenie = WORKSPACES.filter((w) => visibleSections(w, isVisible).length > 0);
  const sekcje = visibleSections(workspace, isVisible);

  // Identyfikatory podłączonych realmów, w kolejności deklaracji dostawców. Pas pokazuje
  // pierwszy; reszta idzie do `title` i na ekran Status połączeń.
  const tozsamosci = auth.providers
    .filter((p) => auth.connected(p.realm))
    .map((p) => auth.realms[p.realm]?.identity?.subject)
    .filter((x): x is string => Boolean(x));

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: 'var(--tlo)' }}>
      {/* ─── SZYNA ─────────────────────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          width: SZEROKOSC_SZYNY,
          flex: 'none',
          background: 'var(--atrament)',
          color: 'var(--tekst-na-ciemnym)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          position: 'relative',
          // Cienka złota nić na krawędzi — jedyne miejsce, gdzie marka dotyka treści.
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '0 0 0 auto',
            width: '1px',
            background:
              'linear-gradient(180deg, transparent, var(--zloto) 18%, var(--zloto) 82%, transparent)',
            opacity: 0.55,
          },
        }}
      >
        {/* POZIOM 1 nawigacji: przejście między PRODUKTAMI TuSzamy. Znak firmowy jest
            wejściem do ekosystemu, a nie tylko ozdobą — to najbardziej naturalne miejsce,
            w którym człowiek szuka „wyjścia do innej aplikacji". */}
        <LauncherProduktow />

        {/*
          POZIOM 2: mapa produktu. Wszystkie cele naraz, pogrupowane — dokładnie jak w makiecie.

          Nagłówek grupy to nazwa przestrzeni roboczej, pozycje to jej sekcje. Model danych
          (przestrzenie → sekcje → uprawnienia → dojrzałość) zostaje bez zmian; zmienia się to,
          ile z niego widać naraz.
        */}
        <Box
          component="nav"
          aria-label="Mapa Control Center"
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            px: 1.5,
            pb: 2,
            // Pasek przewijania w ciemnej szynie wygląda jak rysa — makieta go chowa.
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { width: 0 },
          }}
        >
          {widocznePrzestrzenie.map((w) => {
            const pozycje = visibleSections(w, isVisible);
            if (pozycje.length === 0) return null;
            return (
              <Box key={w.id} component="section" aria-label={w.label}>
                {/*
                  NIE nagłówek. Makieta renderuje `.szyna-grupa` jako zwykły `div` i ma rację:
                  jako `h2` etykieta grupy trafiała do drzewa nagłówków i konkurowała z tytułami
                  ekranów — „Insighty" w szynie i „Insighty" na ekranie stawały się dwoma
                  nagłówkami o tej samej nazwie. Nazwę grupy niesie `aria-label` sekcji wyżej,
                  więc czytnik ekranu nic nie traci.
                */}
                <Typography
                  aria-hidden
                  sx={{
                    px: 1.5,
                    pt: 2,
                    pb: 0.5,
                    fontSize: 'var(--st-mikro)',
                    fontWeight: 700,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: 'rgba(255, 255, 255, 0.34)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {w.label}
                </Typography>

                <Stack component="ul" sx={{ listStyle: 'none', m: 0, p: 0, gap: 0.25 }}>
                  {pozycje.map((section) => {
                    const to = sectionPath(w, section);
                    const aktywna = location.pathname === to;
                    return (
                      <Box component="li" key={`${w.id}-${section.id}`}>
                        <Box
                          component={Link}
                          to={to}
                          aria-current={aktywna ? 'page' : undefined}
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            width: '100%',
                            px: 1.5,
                            py: '10px',
                            borderRadius: 'var(--r-maly)',
                            fontSize: 'var(--st-maly)',
                            fontWeight: 600,
                            color: aktywna ? '#fff' : 'rgba(255,255,255,0.72)',
                            background: aktywna ? 'rgba(247,177,11,0.14)' : 'transparent',
                            textDecoration: 'none',
                            transition: 'background var(--czas-szybko), color var(--czas-szybko)',
                            '&:hover': { background: 'rgba(255,255,255,0.07)', color: '#fff' },
                            /* ZŁOTY GRZBIET — pionowa krecha przy aktywnej pozycji. */
                            '&::before': aktywna
                              ? {
                                  content: '""',
                                  position: 'absolute',
                                  left: 2,
                                  top: 8,
                                  bottom: 8,
                                  width: 3,
                                  borderRadius: '2px',
                                  background: 'var(--zloto)',
                                  boxShadow: '0 0 12px rgba(247, 177, 11, 0.8)',
                                }
                              : undefined,
                          }}
                        >
                          <Box sx={{ width: 20, height: 20, display: 'grid', placeItems: 'center', flex: 'none' }}>
                            <IkonaNawigacji klucz={kluczIkony(w.id, section.id)} />
                          </Box>
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>{section.label}</Box>
                          {section.maturity !== 'live' ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={etykietaDojrzalosci(section.maturity)}
                              sx={{
                                height: 18,
                                fontSize: 9.5,
                                color: 'rgba(255,255,255,0.5)',
                                borderColor: 'rgba(255,255,255,0.2)',
                                background: 'transparent',
                                boxShadow: 'none',
                              }}
                            />
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Box>

        {/*
          STOPKA SZYNY — domknięcie parytetu nawigacji.

          Makieta trzyma tu przełącznik motywu i zwijanie panelu. Produkt daje w tym miejscu to,
          czego makieta mieć nie mogła, bo nie miała prawdziwego wydania: STEMPEL ARTEFAKTU.
          To nie jest ozdoba — przy zgłoszeniu „u mnie wygląda inaczej" pierwsze pytanie brzmi
          „co masz wdrożone", a odpowiedź powinna być pod ręką, nie w osobnym ekranie.

          SHA nie jest sekretem: to publiczny identyfikator commita, który i tak stoi
          w `/cc/build-info.json` obok artefaktu.
        */}
        <Box
          component="footer"
          sx={{
            p: 1.5,
            borderTop: '1px solid var(--linia-na-ciemnym)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 'var(--st-mikro)',
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.34)',
            }}
          >
            {config.environment === 'unknown' ? 'Środowisko nieustawione' : `Środowisko ${config.environment}`}
          </Typography>
          <Typography
            component="a"
            href="#/system"
            sx={{
              fontFamily: 'var(--krój-mono)',
              fontSize: 'var(--st-mikro)',
              color: 'rgba(255,255,255,0.5)',
              textDecoration: 'none',
              '&:hover': { color: '#fff' },
            }}
          >
            {/*
              Rewizja przychodzi z `/api/cc/wydanie`, czyli zza sesji — w publicznym pakiecie
              jej nie ma. Dopóki nie przyjdzie (albo instancja nie ma znacznika), pokazujemy
              wersję aplikacji zamiast wpisywać „unknown", które wygląda jak usterka.
            */}
            {shaWydania ? `wydanie ${shaWydania}` : `wersja ${buildInfo.appVersion}`}
          </Typography>
        </Box>
      </Box>

      {/* ─── KOLUMNA TREŚCI ────────────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            /*
             * JEDEN PAS, BEZ ZAWIJANIA. Po dodaniu wyszukiwarki pasek rozpadał się na dwa wiersze
             * i tożsamość z przyciskiem wylogowania spadały pod spód — makieta ma tu jedną linię.
             * Zamiast zawijać, przycinamy najbardziej elastyczny element (adres zalogowanego).
             * Na wąskich ekranach zawijanie wraca, bo tam jeden pas i tak się nie mieści.
             */
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            px: { xs: 2, md: 4 },
            py: 1.5,
            borderBottom: '1px solid var(--linia)',
            background: 'color-mix(in srgb, var(--tlo) 82%, transparent)',
            backdropFilter: 'blur(14px)',
          }}
        >
          {/* Okruszki zastępują powtórzenie nazwy przestrzeni: ta informacja jest już w szynie,
              a tutaj potrzebna jest ODPOWIEDŹ NA PYTANIE „gdzie jestem", nie drugi zestaw zakładek. */}
          {/*
            Okruszki kurczą się DOPIERO PO wyszukiwarce i skracają wielokropkiem, a nie urywają
            w połowie słowa. `flex: none` (pierwsza wersja tej poprawki) usztywniało pas tak, że
            osiem ekranów o długich nazwach zaczęło przewijać się w POZIOMIE — złapał to test 24.
            Element elastyczny musi być jeden i ma nim być wyszukiwarka, nie odpowiedź na pytanie
            „gdzie jestem".
          */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              flex: '0 1 auto',
              /*
                Dolny próg, a nie `minWidth: 0`. Przy dwóch podłączonych realmach pas robił się
                tak ciasny, że okruszki schodziły do „Start / P…" — czyli do formy, która nie
                odpowiada już na żadne pytanie. Odpowiedź „gdzie jestem" ma prawo do ~180 px
                zanim zacznie się skracać; dopiero potem wielokropek.
              */
              minWidth: { xs: 0, lg: 180 },
            }}
          >
            <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }}>
              {workspace.label}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)' }}>
              /
            </Typography>
            <Typography
              sx={{ fontWeight: 800, fontSize: 15, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {sekcje.find((s) => sectionPath(workspace, s) === location.pathname)?.label ?? 'Przegląd'}
            </Typography>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          {/* Makieta trzyma wyszukiwanie NA WIDOKU, nie tylko pod skrótem. Skrót zna ten, kto
              już wie, że istnieje; przycisk uczy o nim resztę — stąd widoczne „Ctrl K". */}
          <PrzelacznikPaneli />

          <PodgladRestauracji />

          <PrzyciskPalety />

          <PasekSrodowiska />

          {auth.status === 'authenticated' ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
              {/*
                W pasie stoi tożsamość GŁÓWNEGO realmu, a pozostałe podłączenia sygnalizuje „+N".
                Powód: sklejanie wszystkich identyfikatorów w jeden ciąg („operator.local ·
                admin.local@example.invalid") dawało 45 znaków, które wypychały okruszki z pasa.
                Pełna lista nie ginie — zostaje w `title` i na ekranie Status połączeń, gdzie
                jest właściwe miejsce na pytanie „czym dokładnie jestem połączony".
              */}
              <Typography
                variant="body2"
                color="text.secondary"
                title={tozsamosci.join(' · ')}
                sx={{ minWidth: 0, maxWidth: { lg: 220 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {tozsamosci[0] ?? ''}
                {tozsamosci.length > 1 ? ` +${tozsamosci.length - 1}` : ''}
              </Typography>
              {/*
                Po wylogowaniu MUSIMY odesłać na trasę główną.
                Powód (znaleziony w runtime QA na publicznym TEST): trasy /drive i /system celowo
                omijają RequirePermission, więc to one — a nie LoginPage — renderują się po
                wylogowaniu. Użytkownik zostawał w powłoce aplikacji bez żadnego przycisku
                „Zaloguj”; jedynym wyjściem było domyślenie się, że trzeba kliknąć zakładkę Start.
                `replace`, żeby cofnięcie nie wracało na ekran sprzed wylogowania.
              */}
              <Button
                size="small"
                onClick={() => {
                  void auth.signOut().finally(() => navigate('/', { replace: true }));
                }}
              >
                Wyloguj
              </Button>
            </Stack>
          ) : null}
        </Box>

        <PaletaPolecen />

        <PasekDoby />

        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
